<script setup lang="ts">
import { tierIndexOfLevel } from '@/utils/levels';

/**
 * Observa a subida de elo em QUALQUER rota e dispara a celebração.
 *
 * Vive no `app.vue` porque o jogador sobe de elo apostando — num jogo, no
 * esportes, na home. Antes isso morava na página /vip/levels, então a animação
 * praticamente nunca disparava: ninguém ganha XP parado na tela de níveis.
 */
const celebration = useLevelUpCelebrationStore();
const { phase, fromTier, toTier, runId, isOpen, deferredTierIndex } =
  storeToRefs(celebration);

const { isAuthenticated } = storeToRefs(useAuthStore());
const { userId } = useUserData();

const { userInfosProfile } = storeToRefs(useSmarticoUserInfos());

/**
 * Mesma leitura de nível da tela de níveis. Quando isso era calculado aqui
 * dentro, a conta do `userLevelNumber` divergia da da página e a celebração
 * anunciava o elo novo um nível antes da hora — gravando a marca e matando a
 * festa da subida real.
 */
const { level: playerLevel, hasSmarticoLevels } = useSmarticoLevel();

const { isCasinoGamePage } = useCasino();
const { isSportsPage } = useSportsBook();

/**
 * Interromper uma rodada com 4s de tela cheia é pior que atrasar a festa, então
 * dentro de jogo ou do esportes a celebração fica guardada e sai na saída.
 */
const isPlaying = computed(() => isCasinoGamePage.value || isSportsPage.value);

/**
 * `null` enquanto não há perfil nem usuário — aí não há o que comparar.
 *
 * O nível 0 conta como -1 ("Sem elo"): ele não é Bronze na vitrine, então a
 * subida 0 → 1 é a CONQUISTA do Bronze e dispara a celebração — antes os dois
 * caíam no mesmo índice de elo e a primeira festa do jogador nunca acontecia.
 *
 * Também exige a RÉGUA da Smartico, não só o perfil: o perfil chega primeiro, e
 * sem régua o `level` cai no fallback local (`levelAtXp` sobre o `LEVEL_XP`
 * hardcoded), que não bate com a escala real. Isso lia um elo acima do
 * verdadeiro, disparava a festa e gravava a marca — e quando a régua chegava, o
 * elo real, mais baixo, caía no ramo de regressão e REBAIXAVA a marca. Resultado:
 * a animação voltava a cada login, para sempre.
 */
const playerTierIndex = computed(() => {
  if (
    !isAuthenticated.value ||
    !userId.value ||
    !userInfosProfile.value ||
    !hasSmarticoLevels.value
  ) {
    return null;
  }

  if (playerLevel.value === 0) return -1;

  return tierIndexOfLevel(playerLevel.value);
});

watch(
  [playerTierIndex, userId],
  ([current, id]) => {
    if (current === null || !id) return;

    const celebrated = celebration.readCelebratedTier(id);

    // Primeira vez que vemos esta conta: registra onde ela está e não celebra
    // progresso histórico.
    if (celebrated === null) {
      celebration.writeCelebratedTier(id, current);

      return;
    }

    // A marca só sobe. Regressão não deveria acontecer, e realinhar a marca pra
    // baixo (o que fazíamos aqui) não tem upside nenhum: só reabre uma festa já
    // vista. Rebaixar era metade do loop de animação a cada login.
    if (current <= celebrated) return;

    if (isPlaying.value) {
      // Guarda SEM gravar a marca: se o jogador recarregar durante o jogo,
      // queremos detectar a subida de novo em vez de perdê-la calado.
      celebration.defer(current);

      return;
    }

    celebration.play(current);
    celebration.writeCelebratedTier(id, current);
  },
  { immediate: true },
);

// Saiu do jogo: mostra o que ficou guardado.
watch(isPlaying, (playing) => {
  if (playing || deferredTierIndex.value === null || !userId.value) return;

  const pending = deferredTierIndex.value;

  celebration.playDeferred();
  celebration.writeCelebratedTier(userId.value, pending);
});
</script>

<template>
  <Teleport to="body">
    <LevelsLevelUpOverlay
      v-if="isOpen"
      :from-tier="fromTier"
      :phase="phase"
      :run-id="runId"
      :to-tier="toTier"
      @close="celebration.close()"
    />
  </Teleport>
</template>
